import { CircleInfoIcon, InfoIcon, ShowIcon } from '@p2p/assets/svg';
import { Button } from '@p2p/components';
import { TransferHead } from '@p2p/entities/payment';
import { PaymentCancelModal, usePaymentTemplate } from '@p2p/entities/templates';
import {
  TGetOrderResponse,
  TPageSettingsResponse,
  useRoutesInstructionsQuery,
} from '@p2p/services';
import { PaymentType } from '@p2p/shared/constants';
import { formatNumberWithSpaces } from '@p2p/shared/functions';
import { useDeviceDetect } from '@p2p/shared/hooks';
import { routes } from '@p2p/shared/routing';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from '../../styles.module.scss';
import { DeepLinkDesktop, DeepLinkMobile } from '../DeepLink';
import { useDeepLink } from '../DeepLink/useDeepLink';
import { isPayViaLinkOrder, PayByLink } from '../PayByLink';
import { QuickPay } from '../QuickPay';
import { Loader } from './Loader';
import { ModalBankTranfer } from './ModalBankTranfer/ModalBankTranfer';
import { ModalIncrementApplied } from './ModalIncrementApplied/ModalIncrementApplied';
import { Requisites } from './Requisites/Requisites';
import innerStyles from './TransferForm.module.scss';
import { TransferInstruction } from './TransferInstruction';
import { TransferRequisites } from './TransferRequisites';
import { transferTimeNotify } from './TransferTimeNotify';
import { TransferUpload } from './TransferUpload';

const MINUTE = 60;

interface ITransferFormProps {
  order: TGetOrderResponse;
  id: string;
  bankId: number;
  pageSettings: TPageSettingsResponse;
}

export const TransferForm: FC<ITransferFormProps> = ({ order, id, bankId, pageSettings }) => {
  const [isModalIncrementAppliedOpen, setIsModalIncrementAppliedOpen] = useState(true);
  const [isShowNotify, setIsShowNotify] = useState(false);
  const [isShowCancel, setIsShowCancel] = useState(false);
  const [isShowInstruction, setIsShowInstruction] = useState(false);
  const [isShowUpload, setIsShowUpload] = useState(false);
  const { isMobile } = useDeviceDetect();
  const { show, isBaseFlow, isFail, isSberBabilon } = useDeepLink({ order });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { setActiveIndex } = usePaymentTemplate();
  const {
    data: routesInstructions,
    isLoading,
    isError,
  } = useRoutesInstructionsQuery({
    sender_bank: bankId,
    payment_method: order.requisite.payment_type,
    recipient_bank: order.requisite.bank.name,
  });

  const handleSend = () => {
    const transferUploadFiles = JSON.parse(localStorage.getItem('transfer_upload_files') || '{}');
    const files = transferUploadFiles.orderId !== id ? 0 : transferUploadFiles?.files;

    if (transferUploadFiles.orderId !== id) {
      localStorage.removeItem('transfer_upload_files');
    }

    if (pageSettings.receipt && files < 3) {
      setIsShowUpload(true);
    } else {
      navigate(routes.transferSearch.path({ id }));
    }
  };

  useEffect(() => {
    if (isShowNotify) {
      return;
    }

    if (order?.time_left && order?.time_left < MINUTE * 2 && pageSettings.notification) {
      transferTimeNotify({ openInstruction: () => setIsShowInstruction(true) });
      setIsShowNotify(true);
    }
  }, [order?.time_left, pageSettings.notification, isShowNotify]);

  useEffect(() => {
    setActiveIndex(2);
  }, [setActiveIndex]);

  if (isLoading) {
    return <Loader />;
  }

  if (isShowUpload) {
    return <TransferUpload order={order} handleBack={() => setIsShowUpload(false)} />;
  }

  const paymentType = order?.requisite.payment_type;
  // P2P-2808: у части провайдеров (PRAGMATICA, методы mobcom/sbp) реквизита нет —
  // есть только ссылка на QR СБП, а account_number/phone_number/fio реквизита это
  // технические заглушки. Показывать их плательщику нельзя, поэтому блок реквизитов
  // и стандартная инструкция «Оплатить по СБП» скрываются, а вместо них — кнопка
  // «Оплатить» с кастомной инструкцией (см. PayByLink).
  // P2P-2890: и без банковских диплинков «Быстрой оплаты» — на телефоне блок с
  // кнопкой «Оплатить» не сворачивается под диплинк банка плательщика.
  const isPayViaLink = isPayViaLinkOrder(order);

  return (
    <>
      <PaymentCancelModal
        isActive={isShowCancel}
        setIsActive={setIsShowCancel}
        pageSettings={pageSettings}
        orderId={order.id}
        orderStatus={order.status}
      />
      {order?.id && order?.increment_applied && (
        <ModalIncrementApplied
          uid={order?.id}
          sumText={`${order?.expected_income.toLocaleString('ru-RU')} ${order?.currency_code_to}`}
          isCancelModalOpen={isShowCancel}
          showCancelModal={() => setIsShowCancel(true)}
          isOpen={isModalIncrementAppliedOpen}
          setIsOpen={setIsModalIncrementAppliedOpen}
        />
      )}
      {pageSettings.notification &&
        !isShowCancel &&
        !(isModalIncrementAppliedOpen && order?.increment_applied) && (
          <ModalBankTranfer order={order} />
        )}
      <div className={classNames(styles.transfer_dialog_wr, innerStyles.trasferContainer)}>
        <DeepLinkMobile order={order} onSuccess={handleSend} />
        <div className={innerStyles.transferFormWrapper}>
          <div
            className={classNames(
              styles.transfer_dialog,
              styles.transfer_dialog_wide,
              innerStyles.trasfer_dialog,
            )}
          >
            <TransferHead
              time={order?.time_left}
              order={order}
              // P2P-2612 follow-up: для Babilon-M блок "Оплата через СБП" всегда
              // развёрнут на мобильных — не даём его сворачивать в тогл вместе с
              // кнопкой "Оплатить через Сбер" (диплинк тут не гарантированно
              // работает, пользователю сразу нужны реквизиты для ручного перевода).
              toggleMode={isMobile && show && !isSberBabilon && !isPayViaLink}
              open={open}
              setOpen={setOpen}
              isFail={isFail}
            />
            {(open || isBaseFlow || isSberBabilon || isPayViaLink) && (
              <div className={innerStyles.contentWrapper}>
                <DeepLinkDesktop order={order} />
                {/* P2P-2808 follow-up: у payment_via_link провайдеров (PRAGMATICA)
                    реквизит — техническая заглушка, показывать лого банка и
                    "Переведите ... в <банк>" плательщику нельзя — это не то, куда
                    реально уходят деньги (см. PayByLink). */}
                {!isPayViaLink && (
                  <div className={classNames(styles.transfer_dialog__body, styles.left)}>
                    <div className={styles.payment_dialog__platform}>
                      <div className={styles.payment_dialog__platform_wr}>
                        <div className={styles.payment_dialog__platform_image_wr}>
                          <img
                            src={order.bank_svg_image}
                            alt=""
                            className={styles.payment_dialog__platform_image}
                          />
                        </div>
                        <div className={styles.payment_dialog__platform_content}>
                          <div className={styles.payment_dialog__platform_warning}>
                            Переведите {formatNumberWithSpaces(order?.expected_income)} RUB в{' '}
                            {order?.requisite?.bank?.name}. <br />{' '}
                            {paymentType !== PaymentType.PAYMENT_TYPE_TRANSFER &&
                              'Не оставляйте комментарий к переводу'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {isPayViaLink ? (
                  <PayByLink order={order} />
                ) : (
                  <div className={styles.payment_dialog__form}>
                    <Requisites order={order} />
                    <TransferRequisites order={order} />
                  </div>
                )}
                {/* P2P-3036, P2P-3037: темы gazprom_page и ozon_page — кнопка приложения банка под реквизитами. */}
                {!isPayViaLink && <QuickPay order={order} />}
                <div className={styles.attention_block}>
                  <div className={styles.attention_block__wr}>
                    <CircleInfoIcon className={styles.attention_block__icon} />
                    <p className={styles.attention_block__text}>
                      Переведите точную сумму одним платежом. <br /> Реквизиты меняются при каждом
                      платеже.
                    </p>
                  </div>
                </div>
                {!isPayViaLink && !isError && routesInstructions && (
                  <div className={styles.transfer_dialog__instructions}>
                    <div
                      className={classNames(
                        styles.transfer_dialog__readme,
                        innerStyles.instructionBlock,
                        {
                          [innerStyles.openInstruction]: isShowInstruction,
                        },
                      )}
                    >
                      <div className={styles.transfer_dialog__readme_title}>
                        <InfoIcon />
                        Инструкция по оплате
                      </div>
                      <div
                        onClick={() => setIsShowInstruction(!isShowInstruction)}
                        className={classNames(styles.btn, styles.btn_secondary)}
                      >
                        <ShowIcon />
                        {isShowInstruction ? 'Скрыть' : 'Показать'}
                      </div>
                    </div>
                    {isMobile && isShowInstruction && (
                      <TransferInstruction
                        order={order}
                        content={routesInstructions?.content || ''}
                        onClose={() => setIsShowInstruction(false)}
                      />
                    )}
                  </div>
                )}
                <div className={styles.transfer_dialog__actions}>
                  <Button onClick={handleSend}>Я оплатил</Button>
                  <Button onClick={() => setIsShowCancel(true)} variant="outlined">
                    Отменить платёж
                  </Button>
                </div>
              </div>
            )}
          </div>
          {!isPayViaLink && !isError && routesInstructions && !isMobile && isShowInstruction && (
            <div className={classNames(styles.transfer_dialog, innerStyles.trasfer_dialog)}>
              <TransferInstruction
                order={order}
                content={routesInstructions?.content || ''}
                onClose={() => setIsShowInstruction(false)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
